import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Cell, Tooltip,
  PieChart, Pie, Legend,
} from "recharts";
import { Search, Star, RefreshCw, Filter, Trash2 } from "lucide-react";

const RATING_COLORS = ["#DC2626", "#F97316", "#EAB308", "#16A34A", "#1B3A4B"];
const SENTIMENT_COLORS = { positive: "#16A34A", neutral: "#9CA3AF", negative: "#DC2626" };
const SENTIMENT_LABELS = { positive: "Positivo", neutral: "Neutro", negative: "Negativo" };
const FILTERS = [
  { key: "all", label: "Tutte" },
  { key: "positive", label: "😊 Positive" },
  { key: "neutral", label: "😐 Neutre" },
  { key: "negative", label: "😞 Negative" },
  { key: "sleep", label: "🛏 Sonno" },
];

function StatCard({ label, value, hint, accent = "#1B3A4B" }) {
  return (
    <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
      <div className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280] font-semibold">{label}</div>
      <div className="mgx-display text-4xl mt-2" style={{ color: accent }}>{value}</div>
      {hint && <div className="text-xs text-[#6B7280] mt-2">{hint}</div>}
    </div>
  );
}

export default function ReviewsDashboard({ apiEndpoint = "/admin/reviews-dashboard", scope = "admin" }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [hotelFilter, setHotelFilter] = useState("");
  const [feedbackFilter, setFeedbackFilter] = useState("all");
  const [search, setSearch] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const params = scope === "admin" && hotelFilter ? { hotel_id: hotelFilter } : {};
      const { data } = await api.get(apiEndpoint, { params });
      setData(data);
    } catch (e) {
      setError(formatApiError(e));
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, [hotelFilter, apiEndpoint]);

  const refresh = async () => {
    await load();
    toast.success("Dashboard aggiornata");
  };

  const deleteFeedback = async (id) => {
    if (!window.confirm("Vuoi eliminare definitivamente questa recensione?")) return;
    const path = scope === "hotel" ? `/hotel/feedbacks/${id}` : `/admin/feedbacks/${id}`;
    try {
      await api.delete(path);
      setData((prev) => prev ? { ...prev, feedbacks: prev.feedbacks.filter((f) => f.id !== id) } : prev);
      toast.success("Recensione eliminata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };


  const filteredFeedbacks = useMemo(() => {
    if (!data) return [];
    let list = data.feedbacks;
    if (feedbackFilter === "sleep") list = list.filter((f) => f.has_sleep_keyword);
    else if (feedbackFilter !== "all") list = list.filter((f) => f.sentiment === feedbackFilter);
    if (search.trim()) {
      const s = search.toLowerCase();
      list = list.filter((f) => (f.comment || "").toLowerCase().includes(s));
    }
    return list;
  }, [data, feedbackFilter, search]);

  if (loading && !data) return <div className="text-[#6B7280] py-10 text-center">Caricamento dashboard...</div>;
  if (error) return <div className="text-[#DC2626] py-10 text-center">{error}</div>;
  if (!data) return null;

  const ratingData = Object.entries(data.rating_distribution).map(([k, v]) => ({
    name: `${k}★`,
    value: v,
    color: RATING_COLORS[Number(k) - 1],
  }));

  const sentimentData = [
    { name: "Positivo", value: data.sentiment_counts.positive, color: SENTIMENT_COLORS.positive },
    { name: "Neutro", value: data.sentiment_counts.neutral, color: SENTIMENT_COLORS.neutral },
    { name: "Negativo", value: data.sentiment_counts.negative, color: SENTIMENT_COLORS.negative },
  ].filter((d) => d.value > 0);

  const topKeyword = data.sleep_keywords[0];
  const maxKwCount = topKeyword?.count || 1;
  const total = data.total_reviews;

  return (
    <div className="space-y-6" data-testid="reviews-dashboard">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="mgx-display text-4xl text-[#1B3A4B]">Dashboard Recensioni</h2>
          <p className="text-sm text-[#6B7280] mt-2">
            {scope === "admin"
              ? "Analisi globale del sentiment e dei feedback ospiti. Filtra per struttura."
              : `${data.scope?.hotel_name || ""} — Analisi feedback ospiti`}
          </p>
        </div>
        <div className="flex items-center gap-3 flex-wrap">
          {scope === "admin" && (
            <div className="flex items-center gap-2">
              <Filter size={14} className="text-[#6B7280]" />
              <select
                value={hotelFilter}
                onChange={(e) => setHotelFilter(e.target.value)}
                className="text-sm border border-[#E5E7EB] rounded-md px-3 py-2 bg-white"
                data-testid="reviews-hotel-filter"
              >
                <option value="">Tutte le strutture</option>
                {data.hotels?.map((h) => (
                  <option key={h.id} value={h.id}>{h.name} — {h.city}</option>
                ))}
              </select>
            </div>
          )}
          <button onClick={refresh} className="mgx-btn-secondary text-xs gap-2" data-testid="reviews-refresh-btn">
            <RefreshCw size={12} /> Aggiorna
          </button>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-3" data-testid="reviews-kpis">
        <StatCard
          label="Valutazione media"
          value={total ? `${data.avg_rating} / 5` : "—"}
          hint={total ? `su ${total} recensioni` : "Nessun dato"}
          accent="#9333EA"
        />
        <StatCard
          label="Totale recensioni"
          value={total}
          hint={`${data.total_with_comment} con commento`}
        />
        <StatCard
          label="Sentiment positivo"
          value={data.sentiment_counts.positive}
          hint={total ? `${Math.round(data.sentiment_counts.positive / total * 100)}% del totale` : "—"}
          accent="#16A34A"
        />
        <StatCard
          label="Menzioni sonno"
          value={data.sleep_mentions}
          hint={`${data.sleep_keywords.length} parole chiave rilevate`}
          accent="#1B3A4B"
        />
        <StatCard
          label="Guest score (NPS)"
          value={data.nps.score > 0 ? `+${data.nps.score}` : `${data.nps.score}`}
          hint={data.nps.label}
          accent="#C9A66B"
        />
      </div>

      {/* Charts row */}
      <div className="grid lg:grid-cols-2 gap-4">
        {/* Rating distribution */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Distribuzione valutazioni</div>
          <div className="mgx-display text-2xl text-[#1B3A4B] mt-1 mb-4">Stelle per numero di voti</div>
          {total === 0 ? (
            <div className="text-sm text-[#6B7280] py-12 text-center">Nessuna recensione ancora</div>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart data={ratingData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                <XAxis dataKey="name" stroke="#6B7280" fontSize={12} />
                <YAxis stroke="#6B7280" fontSize={12} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="value" radius={[8, 8, 0, 0]}>
                  {ratingData.map((entry, idx) => (
                    <Cell key={idx} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          )}
          <div className="flex gap-3 mt-3 flex-wrap text-xs">
            {ratingData.map((r) => (
              <span key={r.name} className="inline-flex items-center gap-1.5">
                <span className="w-2 h-2 rounded-full" style={{ background: r.color }} />
                {r.name}: <strong>{r.value}</strong>
              </span>
            ))}
          </div>
        </div>

        {/* Sleep keywords */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Parole chiave sonno</div>
          <div className="mgx-display text-2xl text-[#1B3A4B] mt-1 mb-4">Keyword più menzionate</div>
          {data.sleep_keywords.length === 0 ? (
            <div className="text-sm text-[#6B7280] py-12 text-center">Nessuna keyword sonno rilevata nei commenti</div>
          ) : (
            <div className="flex flex-wrap gap-x-5 gap-y-4 items-end" data-testid="sleep-keywords">
              {data.sleep_keywords.map((k) => {
                const intensity = k.count / maxKwCount;
                const fontSize = 13 + intensity * 18;
                const opacity = 0.4 + intensity * 0.6;
                return (
                  <div key={k.name} className="flex flex-col items-start">
                    <span style={{ fontSize, color: "#1B3A4B", fontWeight: 600, opacity, letterSpacing: "-0.02em", lineHeight: 1 }}>
                      {k.name}
                    </span>
                    <span className="text-[11px] text-[#6B7280] mt-1">{k.count}</span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Sentiment + Feedback list */}
      <div className="grid lg:grid-cols-3 gap-4">
        {/* Sentiment donut */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-5">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Analisi sentiment</div>
          <div className="mgx-display text-2xl text-[#1B3A4B] mt-1 mb-4">Tono dei commenti</div>
          {sentimentData.length === 0 ? (
            <div className="text-sm text-[#6B7280] py-12 text-center">Nessun dato</div>
          ) : (
            <>
              <ResponsiveContainer width="100%" height={200}>
                <PieChart>
                  <Pie data={sentimentData} dataKey="value" nameKey="name" innerRadius={50} outerRadius={80} paddingAngle={2}>
                    {sentimentData.map((entry, idx) => (
                      <Cell key={idx} fill={entry.color} />
                    ))}
                  </Pie>
                  <Tooltip contentStyle={{ background: "#FFFFFF", border: "1px solid #E5E7EB", borderRadius: 8, fontSize: 12 }} />
                </PieChart>
              </ResponsiveContainer>
              <div className="space-y-3 mt-2">
                {["positive", "neutral", "negative"].map((key) => {
                  const count = data.sentiment_counts[key];
                  const pct = total ? Math.round(count / total * 100) : 0;
                  const withSleep = data.sentiment_with_sleep[key];
                  return (
                    <div key={key}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="font-medium" style={{ color: SENTIMENT_COLORS[key] }}>
                          {key === "positive" ? "😊" : key === "neutral" ? "😐" : "😞"} {SENTIMENT_LABELS[key]}
                        </span>
                        <span className="text-[#6B7280]">{count} ({pct}%)</span>
                      </div>
                      <div className="h-1.5 bg-[#F1F2F4] rounded-full overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${pct}%`, background: SENTIMENT_COLORS[key] }} />
                      </div>
                      {withSleep > 0 && (
                        <div className="text-[10px] text-[#6B7280] mt-1">{withSleep} con keyword sonno</div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Feedback list (filterable) */}
        <div className="bg-white border border-[#E5E7EB] rounded-xl p-5 lg:col-span-2">
          <div className="text-[11px] uppercase tracking-[0.2em] text-[#6B7280] font-semibold">Recensioni ospiti</div>
          <div className="mgx-display text-2xl text-[#1B3A4B] mt-1 mb-4">Tutti i feedback</div>

          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {FILTERS.map((f) => {
              const active = feedbackFilter === f.key;
              return (
                <button
                  key={f.key}
                  onClick={() => setFeedbackFilter(f.key)}
                  className="px-3 py-1.5 text-xs rounded-full border transition-colors"
                  style={{
                    borderColor: active ? "#1B3A4B" : "#E5E7EB",
                    background: active ? "#1B3A4B" : "transparent",
                    color: active ? "#FFFFFF" : "#1A1C20",
                  }}
                  data-testid={`feedback-filter-${f.key}`}
                >
                  {f.label}
                </button>
              );
            })}
            <div className="ml-auto flex items-center gap-2 border border-[#E5E7EB] rounded-md px-3 py-1.5 bg-white">
              <Search size={14} className="text-[#6B7280]" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca nei commenti..."
                className="outline-none text-sm w-44"
                data-testid="feedback-search"
              />
            </div>
          </div>

          <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1" data-testid="feedback-list">
            {filteredFeedbacks.length === 0 ? (
              <div className="text-sm text-[#6B7280] py-10 text-center">Nessuna recensione corrisponde ai filtri</div>
            ) : (
              filteredFeedbacks.map((f) => {
                const sColor = SENTIMENT_COLORS[f.sentiment];
                return (
                  <div key={f.id} className="border border-[#E5E7EB] rounded-lg p-4">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="flex items-center gap-2">
                        <div className="flex" style={{ color: "#EAB308" }}>
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} size={14} fill={i < f.rating ? "#EAB308" : "transparent"} />
                          ))}
                        </div>
                        <span className="text-xs text-[#6B7280] font-medium">{f.rating}/5</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <span
                          className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold"
                          style={{ color: sColor, border: `1px solid ${sColor}40`, background: `${sColor}10` }}
                        >
                          {SENTIMENT_LABELS[f.sentiment]}
                        </span>
                        {f.has_sleep_keyword && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full uppercase tracking-wider font-semibold" style={{ color: "#9333EA", background: "#9333EA15" }}>
                            🛏 Sonno
                          </span>
                        )}
                        <span className="text-[11px] text-[#6B7280] uppercase">{f.language}</span>
                        <span className="text-[11px] text-[#6B7280]">{new Date(f.created_at).toLocaleString("it-IT", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })}</span>
                      </div>
                    </div>
                    {f.comment && (
                      <p className="text-sm text-[#1A1C20] mt-2 leading-relaxed">{f.comment}</p>
                    )}
                    {(scope === "admin" || scope === "hotel") && (
                      <div className="flex justify-end mt-3">
                        <button
                          onClick={() => deleteFeedback(f.id)}
                          data-testid={`delete-feedback-${f.id}`}
                          className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-[#6B7280] hover:text-[#E30613] transition-colors"
                        >
                          <Trash2 size={12} />
                          Elimina
                        </button>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
